import './SocialAuthButtons.css'

import { useState } from 'react'

import { Button } from './Button'
import {
  isFacebookAuthConfigured,
  isGoogleAuthConfigured,
  signInWithProvider,
  type SocialAuthTokens,
  type SocialProvider,
} from '../lib/social-auth'

export type SocialAuthButtonsProps = {
  mode: 'sign-in' | 'sign-up'
  disabled?: boolean
  onTokens: (tokens: SocialAuthTokens) => void | Promise<void>
  onError?: (message: string) => void
}

export function SocialAuthButtons(props: SocialAuthButtonsProps) {
  const { mode, disabled, onTokens, onError } = props
  const [busyProvider, setBusyProvider] = useState<SocialProvider | null>(null)

  const googleEnabled = isGoogleAuthConfigured()
  const facebookEnabled = isFacebookAuthConfigured()

  if (!googleEnabled && !facebookEnabled) return null

  const actionLabel = mode === 'sign-in' ? 'Continue with' : 'Sign up with'
  const isBusy = !!busyProvider || !!disabled

  const handleProvider = async (provider: SocialProvider) => {
    if (isBusy) return
    setBusyProvider(provider)
    try {
      const tokens = await signInWithProvider(provider)
      await onTokens(tokens)
    } catch (error) {
      const message = error instanceof Error ? error.message : `${provider} sign-in failed`
      // User closed the popup — keep quiet unless it's a real failure.
      if (!/cancel/i.test(message)) onError?.(message)
    } finally {
      setBusyProvider(null)
    }
  }

  return (
    <div className="social-auth-buttons">
      {googleEnabled && (
        <Button
          className="social-auth-buttons__button social-auth-buttons__button--google"
          kind="secondary"
          leftIcon="chrome"
          disabled={isBusy}
          onClick={() => handleProvider('google')}
          text={busyProvider === 'google' ? 'Connecting…' : `${actionLabel} Google`}
        />
      )}
      {facebookEnabled && (
        <Button
          className="social-auth-buttons__button social-auth-buttons__button--facebook"
          kind="secondary"
          leftIcon="facebook"
          disabled={isBusy}
          onClick={() => handleProvider('facebook')}
          text={busyProvider === 'facebook' ? 'Connecting…' : `${actionLabel} Facebook`}
        />
      )}
    </div>
  )
}
